import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "../App";
import { Button } from "../components/ui/button";
import {
  ArrowLeft,
  Moon,
  Droplets,
  Dumbbell,
  Zap,
  Brain,
  Save,
  TrendingUp,
  Calendar,
} from "lucide-react";
import { toast } from "sonner";

const WellnessPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [form, setForm] = useState({
    sleep_hours: "",
    water_glasses: "",
    exercise_minutes: "",
    energy_level: 5,
    stress_level: 5,
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [summary, setSummary] = useState(null);
  const [todayLog, setTodayLog] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [todayRes, summaryRes] = await Promise.all([
        api.get("/wellness/today"),
        api.get("/wellness/summary"),
      ]);
      if (todayRes.data && todayRes.data.wellness_id) {
        setTodayLog(todayRes.data);
        setForm({
          sleep_hours: todayRes.data.sleep_hours || "",
          water_glasses: todayRes.data.water_glasses || "",
          exercise_minutes: todayRes.data.exercise_minutes || "",
          energy_level: todayRes.data.energy_level || 5,
          stress_level: todayRes.data.stress_level || 5,
          notes: todayRes.data.notes || "",
        });
      }
      setSummary(summaryRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post("/wellness/log", {
        sleep_hours: form.sleep_hours ? parseFloat(form.sleep_hours) : null,
        water_glasses: form.water_glasses ? parseInt(form.water_glasses) : null,
        exercise_minutes: form.exercise_minutes ? parseInt(form.exercise_minutes) : null,
        energy_level: form.energy_level,
        stress_level: form.stress_level,
        notes: form.notes,
      });
      toast.success("Wellness logged! This data will personalize your guidance.");
      setTodayLog(true);
    } catch (err) {
      toast.error("Failed to save wellness data");
    } finally {
      setSaving(false);
    }
  };

  const SliderInput = ({ label, icon: Icon, value, onChange, min, max, color, unit }) => (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className={`w-4 h-4 ${color}`} />
          <span className="text-white/80 text-sm font-body">{label}</span>
        </div>
        <span className={`text-sm font-body font-medium ${color}`}>{value}{unit}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={1}
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value))}
        className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
        style={{
          background: `linear-gradient(to right, ${color === 'text-green-400' ? '#4ade80' : color === 'text-red-400' ? '#f87171' : '#D4AF37'} 0%, ${color === 'text-green-400' ? '#4ade80' : color === 'text-red-400' ? '#f87171' : '#D4AF37'} ${(value / max) * 100}%, rgba(255,255,255,0.1) ${(value / max) * 100}%, rgba(255,255,255,0.1) 100%)`,
        }}
      />
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#070B14" }}>
        <div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "#070B14" }} data-testid="wellness-page">
      <header className="sticky top-0 z-50 bg-[#070B14]/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-3">
          <Button variant="ghost" onClick={() => navigate("/dashboard")} className="text-white/50 hover:text-white p-2" data-testid="wellness-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="font-display text-xl text-white">Wellness Tracker</h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        {/* Summary Cards */}
        {summary?.has_data && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 animate-fade-in-up" data-testid="wellness-summary">
            <div className="glass-card p-4 text-center">
              <Moon className="w-5 h-5 text-[#8B5CF6] mx-auto mb-1" />
              <p className="text-white font-display text-xl">{summary.avg_sleep}h</p>
              <p className="text-white/40 text-xs font-body">Avg Sleep</p>
            </div>
            <div className="glass-card p-4 text-center">
              <Zap className="w-5 h-5 text-green-400 mx-auto mb-1" />
              <p className="text-white font-display text-xl">{summary.avg_energy}/10</p>
              <p className="text-white/40 text-xs font-body">Avg Energy</p>
            </div>
            <div className="glass-card p-4 text-center">
              <Brain className="w-5 h-5 text-red-400 mx-auto mb-1" />
              <p className="text-white font-display text-xl">{summary.avg_stress}/10</p>
              <p className="text-white/40 text-xs font-body">Avg Stress</p>
            </div>
            <div className="glass-card p-4 text-center">
              <Calendar className="w-5 h-5 text-[#D4AF37] mx-auto mb-1" />
              <p className="text-white font-display text-xl">{summary.days_tracked}</p>
              <p className="text-white/40 text-xs font-body">Days Tracked</p>
            </div>
          </div>
        )}

        {/* Daily Log Form */}
        <div className="glass-card p-6 space-y-6 animate-fade-in-up" data-testid="wellness-form">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-[#D4AF37]" />
            <h2 className="font-display text-lg text-white">
              {todayLog ? "Update Today's Log" : "Log Today's Wellness"}
            </h2>
          </div>

          <div className="grid sm:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <label className="flex items-center gap-2 text-white/60 text-sm font-body">
                <Moon className="w-4 h-4 text-[#8B5CF6]" /> Sleep (hours)
              </label>
              <input
                data-testid="sleep-input"
                type="number"
                step="0.5"
                min="0"
                max="24"
                value={form.sleep_hours}
                onChange={(e) => setForm({ ...form, sleep_hours: e.target.value })}
                placeholder="7.5"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white font-body text-sm placeholder-white/20 focus:outline-none focus:border-[#8B5CF6]/50"
              />
            </div>
            <div className="space-y-1.5">
              <label className="flex items-center gap-2 text-white/60 text-sm font-body">
                <Droplets className="w-4 h-4 text-blue-400" /> Water (glasses)
              </label>
              <input
                data-testid="water-input"
                type="number"
                min="0"
                max="30"
                value={form.water_glasses}
                onChange={(e) => setForm({ ...form, water_glasses: e.target.value })}
                placeholder="8"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white font-body text-sm placeholder-white/20 focus:outline-none focus:border-blue-500/50"
              />
            </div>
            <div className="space-y-1.5">
              <label className="flex items-center gap-2 text-white/60 text-sm font-body">
                <Dumbbell className="w-4 h-4 text-orange-400" /> Exercise (min)
              </label>
              <input
                data-testid="exercise-input"
                type="number"
                min="0"
                max="300"
                value={form.exercise_minutes}
                onChange={(e) => setForm({ ...form, exercise_minutes: e.target.value })}
                placeholder="30"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white font-body text-sm placeholder-white/20 focus:outline-none focus:border-orange-500/50"
              />
            </div>
          </div>

          <SliderInput
            label="Energy Level"
            icon={Zap}
            value={form.energy_level}
            onChange={(v) => setForm({ ...form, energy_level: v })}
            min={1} max={10} color="text-green-400" unit="/10"
          />
          <SliderInput
            label="Stress Level"
            icon={Brain}
            value={form.stress_level}
            onChange={(v) => setForm({ ...form, stress_level: v })}
            min={1} max={10} color="text-red-400" unit="/10"
          />

          <div className="space-y-1.5">
            <label className="text-white/60 text-sm font-body">Notes (optional)</label>
            <textarea
              data-testid="wellness-notes"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="How are you feeling today?"
              rows={2}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white font-body text-sm placeholder-white/20 focus:outline-none focus:border-[#D4AF37]/50 resize-none"
            />
          </div>

          <Button
            data-testid="save-wellness-btn"
            onClick={handleSave}
            disabled={saving}
            className="btn-gold w-full py-5"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Saving..." : todayLog ? "Update Wellness Log" : "Save Wellness Log"}
          </Button>
          <p className="text-center text-white/30 text-xs font-body">
            Your wellness data personalizes your daily guidance for better, tailored advice.
          </p>
        </div>

        {/* Weekly Trend */}
        {summary?.has_data && summary.recent_logs?.length > 1 && (
          <div className="glass-card p-6 animate-fade-in-up" data-testid="wellness-trend">
            <h3 className="font-display text-lg text-white mb-4">7-Day Trend</h3>
            <div className="space-y-3">
              {summary.recent_logs.slice(0, 7).reverse().map((log) => (
                <div key={log.date} className="flex items-center gap-3 text-sm">
                  <span className="text-white/40 font-body w-20">{new Date(log.date + "T12:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}</span>
                  <div className="flex-1 flex items-center gap-2">
                    {log.sleep_hours && (
                      <span className="px-2 py-0.5 rounded bg-[#8B5CF6]/10 text-[#C4B5FD] text-xs">{log.sleep_hours}h sleep</span>
                    )}
                    {log.energy_level && (
                      <span className="px-2 py-0.5 rounded bg-green-500/10 text-green-400 text-xs">E:{log.energy_level}</span>
                    )}
                    {log.stress_level && (
                      <span className="px-2 py-0.5 rounded bg-red-500/10 text-red-400 text-xs">S:{log.stress_level}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default WellnessPage;
